import React, { useState, useEffect } from 'react';
import { formatDate, formatDateForInput, getCurrentDateForInput, getFutureDateForInput } from '../utils/dateUtils';
import GoogleMapsAddressInput from '../components/GoogleMapsAddressInput';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  ShoppingCart as OrderIcon,
  FindInPage as FindInPageIcon,
  CheckCircle as CheckCircleIcon,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';

import { useAuth } from '../context/AuthContext';
import EnhancedSearch from '../components/common/EnhancedSearch';
import config, { API_URL } from '../config';
import AdvertisingOrderForm, { checkOrderTopSpotDurationRequirements } from '../components/AdvertisingOrderForm';
import NotesDialog from '../components/NotesDialog';
import { collectAllNotesFromEnquiry } from '../utils/noteUtils';

const WEBSITE_GENERAL_SERVICE_LABELS = {
  ePublisher: 'ePublisher Platform',
  advertising: 'Advertising',
  printDesign: 'Design+Print'
};

const WEBSITE_GENERAL_HEAR_LABELS = {
  google: 'Google',
  facebook: 'Facebook',
  referral: 'Referral',
  school: 'Current School',
  other: 'Other'
};

/**
 * Enquiries Page
 * Manages all enquiries with comprehensive tracking
 */
const Enquiries = () => {
  const { getToken, user } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [enquiryListTab, setEnquiryListTab] = useState('sales');

  // School Enquiries state
  const [schoolEnquiries, setSchoolEnquiries] = useState([]);
  const [schoolEnquiriesLoading, setSchoolEnquiriesLoading] = useState(false);
  const [schoolEnquiriesSearch, setSchoolEnquiriesSearch] = useState('');
  const [schoolEnquiriesStatusFilter, setSchoolEnquiriesStatusFilter] = useState('all');
  const [schoolEnquiryDialog, setSchoolEnquiryDialog] = useState({ open: false, editing: null });
  const [schoolEnquiryForm, setSchoolEnquiryForm] = useState({
    school_name: '', products: [], contact_person: '', phone: '', email: '', status: 'New', notes: ''
  });
  const [schoolEnquiryDeleteDialog, setSchoolEnquiryDeleteDialog] = useState({ open: false, id: null });
  const [demoDateDialog, setDemoDateDialog] = useState({ open: false, enquiry: null, date: '' });
  const [createOrderDialog, setCreateOrderDialog] = useState({ open: false, enquiry: null, loading: false, formUrl: null, orderId: null });
  const [cancelEnquiryDialog, setCancelEnquiryDialog] = useState({ open: false, enquiry: null });
  const [websiteGeneralEnquiries, setWebsiteGeneralEnquiries] = useState([]);
  const [loadingWebsiteGeneral, setLoadingWebsiteGeneral] = useState(false);
  const [generalStatusFilter, setGeneralStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('1'); // Default to 1 month
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Default to current month (YYYY-MM format)
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Notes dialog state
  const [notesDialog, setNotesDialog] = useState({
    open: false,
    title: '',
    entityType: '',
    entityId: '',
    notes: []
  });
  
  // Convert to Business dialog state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [enquiryToConvert, setEnquiryToConvert] = useState(null);
  const [organizationFormData, setOrganizationFormData] = useState({
    name: '',
    businessType: '',
    primaryName: '',
    primaryEmail: '',
    primaryPhone: '',
    generalEmail: '',
    generalPhone: '',
    address: '',
    formattedAddress: '',
    suburb: '',
    state: '',
    postcode: '',
    latitude: null,
    longitude: null,
    notes: '',
    isBusiness: true,
    isSchool: false,
    isActive: true
  });

  // Create Order dialog state
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
  const [enquiryForOrder, setEnquiryForOrder] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  // Full org object for the business typeahead. Search hits the server, so this
  // does not depend on the paged local `organizations` list.
  const [selectedEnquiryOrg, setSelectedEnquiryOrg] = useState(null);
  
  // Sales Consultant assignment confirmation dialog state
  const [consultantConfirmDialog, setConsultantConfirmDialog] = useState({
    open: false,
    enquiryId: null,
    consultantId: null,
    consultantName: ''
  });

  // Find Business dialog state
  const [findBusinessDialog, setFindBusinessDialog] = useState({
    open: false,
    enquiry: null
  });
  const [findBusinessSearch, setFindBusinessSearch] = useState('');
  const [findBusinessSearchResults, setFindBusinessSearchResults] = useState([]);
  const [findBusinessSearchLoading, setFindBusinessSearchLoading] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [orderFormData, setOrderFormData] = useState({
    organization_id: '',
    business_number: '',
    business_name: '',
    business_type: '',
    orderType: 'ad',
    productType: '',
    notes: '',
    designNotes: '',
    audiences: [{
      organization_id: '',
      channel: '',
      adType: '',
      adGroup: 1,
      startDate: '',
      endDate: '',
      price: '',
      discount: '0',
      discountType: '$',
      eLinkType: 'None',
      eLinkContent: ''
    }]
  });

  // Helper function to check if addon duration requirements are met for UI validation
  const checkAddonDurationRequirements = (audiences = []) => {
    for (const audience of audiences) {
      if (audience.addons && audience.addons.topSpot) {
        if (!audience.startDate || !audience.endDate) {
          return false; // Missing dates
        }
        
        const startDate = new Date(audience.startDate);
        const endDate = new Date(audience.endDate);
        const diffTime = Math.abs(endDate - startDate);
        const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
        
        if (diffWeeks < 12) {
          return false; // Top Spot requires minimum 12 weeks
        }
      }
      
      // Add other addon validations here as needed
      // if (audience.addons.someOtherAddon && someCondition) { return false; }
    }
    return true; // All addon requirements met
  };

  // Form state
  const [formData, setFormData] = useState({
    date: getCurrentDateForInput(),
    leadMethod: '',
    outcome: 'New', // Default to 'New'
    followUpDate: '',
    followUpNotes: '',
    whyNotInterested: '',
    businessName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    campaignType: '',
    advertiserInDatabase: true, // Default to Yes
    organizationId: '', // Store the actual organization ID
    salesConsultant: '',
    invoiceAmount: '',
    salesNotes: '',
    customerNotes: ''
  });

  const leadMethods = [
    'Website',
    'Phone Call',
    'Email',
    'School Referral'
  ];

  const outcomes = [
    'New',
    'Follow Up Later',
    'Offered',
    'Not Interested',
    'Cancelled After Order'
  ];

  // Not interested reasons
  const notInterestedReasons = [
    'Only Want One-Off or Short Term Ad',
    'Too Expensive',
    "Don't Have Requested School/s",
    'School/s Full',
    'No Schools in Area',
    'Non-Responsive to Contact',
    'Chose to Advertise Elsewhere',
    'Seeking Quotes Only'
  ];

  const [salesConsultants, setSalesConsultants] = useState([]);

  useEffect(() => {
    loadEnquiries();
    loadSalesConsultants();
    loadOrganizations();
  }, []);

  const loadWebsiteGeneralEnquiries = async () => {
    setLoadingWebsiteGeneral(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (generalStatusFilter && generalStatusFilter !== 'all') {
        params.set('status', generalStatusFilter);
      }
      params.set('limit', '100');
      const response = await fetch(`${API_URL}/website-general-enquiries?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setWebsiteGeneralEnquiries(result.data || []);
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to load website general enquiries',
          severity: 'error'
        });
        setWebsiteGeneralEnquiries([]);
      }
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Failed to load website general enquiries', severity: 'error' });
      setWebsiteGeneralEnquiries([]);
    } finally {
      setLoadingWebsiteGeneral(false);
    }
  };

  useEffect(() => {
    if (enquiryListTab === 'website_general') {
      loadWebsiteGeneralEnquiries();
    }
    if (enquiryListTab === 'school_enquiries') {
      loadSchoolEnquiries();
    }
  }, [enquiryListTab, generalStatusFilter]);

  const loadSchoolEnquiries = async () => {
    setSchoolEnquiriesLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (schoolEnquiriesStatusFilter && schoolEnquiriesStatusFilter !== 'all') {
        params.set('status', schoolEnquiriesStatusFilter);
      }
      if (schoolEnquiriesSearch) params.set('search', schoolEnquiriesSearch);
      const response = await fetch(`${API_URL}/school-enquiries?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setSchoolEnquiries(result.data || []);
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to load school enquiries', severity: 'error' });
        setSchoolEnquiries([]);
      }
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Failed to load school enquiries', severity: 'error' });
      setSchoolEnquiries([]);
    } finally {
      setSchoolEnquiriesLoading(false);
    }
  };

  useEffect(() => {
    if (enquiryListTab === 'school_enquiries') loadSchoolEnquiries();
  }, [schoolEnquiriesStatusFilter]);

  const handleOpenSchoolEnquiryDialog = (enquiry = null) => {
    if (enquiry) {
      const productsArr = enquiry.products
        ? enquiry.products.split(',').map(p => p.trim()).filter(Boolean)
        : [];
      setSchoolEnquiryForm({
        school_name: enquiry.school_name || '',
        products: productsArr,
        contact_person: enquiry.contact_person || '',
        phone: enquiry.phone || '',
        email: enquiry.email || '',
        status: enquiry.status || 'New',
        notes: enquiry.notes || ''
      });
      setSchoolEnquiryDialog({ open: true, editing: enquiry });
    } else {
      setSchoolEnquiryForm({ school_name: '', products: [], contact_person: '', phone: '', email: '', status: 'New', notes: '' });
      setSchoolEnquiryDialog({ open: true, editing: null });
    }
  };

  const handleSaveSchoolEnquiry = async () => {
    if (!schoolEnquiryForm.school_name.trim()) {
      setSnackbar({ open: true, message: 'School name is required', severity: 'error' });
      return;
    }
    try {
      const token = await getToken();
      const editing = schoolEnquiryDialog.editing;
      const url = editing
        ? `${API_URL}/school-enquiries/${editing.id}`
        : `${API_URL}/school-enquiries`;
      const method = editing ? 'PUT' : 'POST';
      const payload = {
        ...schoolEnquiryForm,
        products: Array.isArray(schoolEnquiryForm.products)
          ? schoolEnquiryForm.products.join(', ')
          : schoolEnquiryForm.products
      };
      const response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setSnackbar({ open: true, message: editing ? 'School enquiry updated' : 'School enquiry added', severity: 'success' });
        setSchoolEnquiryDialog({ open: false, editing: null });
        loadSchoolEnquiries();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to save', severity: 'error' });
      }
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Failed to save school enquiry', severity: 'error' });
    }
  };

  const handleDeleteSchoolEnquiry = async () => {
    const id = schoolEnquiryDeleteDialog.id;
    if (!id) return;
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/school-enquiries/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setSnackbar({ open: true, message: 'School enquiry deleted', severity: 'success' });
        setSchoolEnquiryDeleteDialog({ open: false, id: null });
        loadSchoolEnquiries();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to delete', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete school enquiry', severity: 'error' });
    }
  };

  // Quick status update (e.g. New ? Contacted)
  const handleQuickStatusChange = async (enquiry, newStatus) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/school-enquiries/${enquiry.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setSnackbar({ open: true, message: `Status updated to ${newStatus}`, severity: 'success' });
        loadSchoolEnquiries();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to update status', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  // Confirm setting Demo status with a date
  const handleConfirmDemoDate = async () => {
    const { enquiry, date } = demoDateDialog;
    if (!enquiry || !date) return;
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/school-enquiries/${enquiry.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'Demo', demo_date: date })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setSnackbar({ open: true, message: 'Status set to Demo', severity: 'success' });
        setDemoDateDialog({ open: false, enquiry: null, date: '' });
        loadSchoolEnquiries();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to set demo', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to set demo status', severity: 'error' });
    }
  };

  // Create school order and mark as Ordered
  const handleCreateOrderFromEnquiry = async () => {
    const enquiry = createOrderDialog.enquiry;
    if (!enquiry) return;
    setCreateOrderDialog(prev => ({ ...prev, loading: true }));
    try {
      const token = await getToken();
      // Generate the school signup form / order
      const orderRes = await fetch(`${API_URL}/school-orders/generate-new-school-form`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ schoolName: enquiry.school_name, googleMapsApiKey: config.googleMapsApiKey || '' })
      });
      const orderResult = await orderRes.json();
      if (!orderRes.ok || !orderResult.success) {
        throw new Error(orderResult.message || 'Failed to create school order');
      }
      const formUrl = orderResult.formUrl;
      const orderId = orderResult.orderId;

      // Copy link to clipboard
      try { await navigator.clipboard.writeText(formUrl); } catch {}

      // Update enquiry status to Ordered � pass orderId so new_schools can link back to the order
      await fetch(`${API_URL}/school-enquiries/${enquiry.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'Ordered', school_order_id: orderId })
      });

      setCreateOrderDialog(prev => ({ ...prev, loading: false, formUrl, orderId }));
      loadSchoolEnquiries();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to create school order', severity: 'error' });
      setCreateOrderDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Cancel an enquiry
  const handleConfirmCancelEnquiry = async () => {
    const enquiry = cancelEnquiryDialog.enquiry;
    if (!enquiry) return;
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/school-enquiries/${enquiry.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'Cancelled' })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setSnackbar({ open: true, message: 'Enquiry cancelled', severity: 'info' });
        setCancelEnquiryDialog({ open: false, enquiry: null });
        loadSchoolEnquiries();
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to cancel', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to cancel enquiry', severity: 'error' });
    }
  };

  const handleWebsiteGeneralStatusChange = async (id, status) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/website-general-enquiries/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setWebsiteGeneralEnquiries((prev) =>
          prev.map((row) => (row.id === id ? { ...row, status } : row))
        );
        setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Could not update status',
          severity: 'error'
        });
      }
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Could not update status', severity: 'error' });
    }
  };

  const loadSalesConsultants = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/users`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        const users = result.data || result || [];
        // Include all users as potential sales consultants
        const salesUsers = Array.isArray(users) ? users : [];
        setSalesConsultants(salesUsers);
      }
    } catch (error) {
      console.error('Error loading sales consultants:', error);
      // Fallback to current user if API fails
      setSalesConsultants([{ id: user?.id || user?.sub, name: user?.name || 'Current User' }]);
    }
  };

  const loadOrganizations = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/organizations`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        // Organisations API returns array directly, not wrapped in { data: [...] }
        const orgs = Array.isArray(result) ? result : result.data || [];
        setOrganizations(orgs);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to load organizations. Check console for details.', 
        severity: 'error' 
      });
    }
  };

  const loadEnquiries = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/enquiries`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setEnquiries(result.data || []);
      } else {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`Failed to fetch enquiries: ${response.status}`);
      }
    } catch (error) {
      console.error('Error loading enquiries:', error);
      setSnackbar({ open: true, message: `Error loading enquiries: ${error.message}`, severity: 'error' });
      // Fallback to empty array if API fails
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (enquiry = null) => {
    if (enquiry) {
      setEditingEnquiry(enquiry);
      setFormData({ 
        ...enquiry, 
        date: enquiry.date ? formatDateForInput(enquiry.date) : getCurrentDateForInput(),
        followUpDate: enquiry.followUpDate ? formatDateForInput(enquiry.followUpDate) : '',
        followUpNotes: enquiry.followUpNotes || '',
        salesConsultant: (enquiry.salesConsultantId && enquiry.salesConsultantId !== 'Unknown') ? enquiry.salesConsultantId : (enquiry.salesConsultant && enquiry.salesConsultant !== 'Unknown') ? enquiry.salesConsultant : '',
        // Convert string Yes/No to boolean for the dropdown
        advertiserInDatabase: enquiry.advertiserInDatabase === 'Yes' || enquiry.advertiserInDatabase === true,
        // Include organization ID if available
        organizationId: enquiry.organizationId || '',
        // Ensure null values become empty strings for form fields
        whyNotInterested: enquiry.whyNotInterested || '',
        email: enquiry.email || '',
        phone: enquiry.phone || '',
        campaignType: enquiry.campaignType || '',
        invoiceAmount: enquiry.invoiceAmount || '',
        salesNotes: enquiry.salesNotes || '',
        customerNotes: enquiry.customerNotes || ''
      });
      const inDatabase = enquiry.advertiserInDatabase === 'Yes' || enquiry.advertiserInDatabase === true;
      setSelectedEnquiryOrg(
        inDatabase && enquiry.organizationId
          ? {
              id: enquiry.organizationId,
              name: enquiry.linkedOrganizationName || enquiry.businessName || '',
              isBusiness: true
            }
          : null
      );
    } else {
      setEditingEnquiry(null);
      setSelectedEnquiryOrg(null);
      setFormData({
        date: getCurrentDateForInput(),
        leadMethod: '',
        outcome: 'New', // Default to 'New'
        followUpDate: '',
        followUpNotes: '',
        whyNotInterested: '',
        businessName: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        campaignType: '',
        advertiserInDatabase: true, // Default to Yes
        organizationId: '',
        salesConsultant: user?.id || '',
        invoiceAmount: '',
        salesNotes: '',
        customerNotes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEnquiry(null);
    setSelectedEnquiryOrg(null);
  };

  // Handle opening notes dialog
  const handleOpenNotesDialog = (entityType, entityId, title, existingNotes = []) => {
    setNotesDialog({
      open: true,
      title,
      entityType,
      entityId,
      notes: existingNotes
    });
  };

  // Handle closing notes dialog
  const handleCloseNotesDialog = () => {
    setNotesDialog({
      open: false,
      title: '',
      entityType: '',
      entityId: '',
      notes: []
    });
    
    // Force a small state update to refresh the UI and note counts
    setEnquiries(prev => [...prev]);
  };

  // Helper function to get total note count including localStorage
  const getTotalNoteCount = (enquiry) => {
    // Count database notes
    let dbNoteCount = 0;
    if (enquiry.salesNotes) dbNoteCount++;
    if (enquiry.customerNotes) dbNoteCount++;
    if (enquiry.followUpNotes) dbNoteCount++;
    
    // Count localStorage notes
    const storageKey = `notes_enquiry_${enquiry.id}`;
    const storageNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    return dbNoteCount + storageNotes.length;
  };

  // Handle notes update - update local state to reflect new note count
  const handleNotesUpdate = async (updatedNotes, newNote) => {
    
    // Update the local enquiry state to trigger UI refresh
    setEnquiries(prev => prev.map(enq => 
      enq.id.toString() === notesDialog.entityId 
        ? { 
            ...enq, 
            // Update note counts for UI display
            salesNotes: enq.salesNotes || 'Has notes',
            customerNotes: enq.customerNotes,
            _noteCount: updatedNotes.length // Add hidden field for count
          } 
        : enq
    ));
    
    setSnackbar({ 
      open: true, 
      message: 'Note added successfully!', 
      severity: 'success' 
    });
    
    // Try to reload from backend in background (for when server is working)
    try {
      await loadEnquiries();
    } catch (error) {
    }
  };

  // Validation function to check if all required fields are filled
  const isFormValid = () => {
    // Base required fields for all enquiries
    const baseFieldsValid = 
      formData.leadMethod?.trim() &&
      formData.businessName?.trim() &&
      formData.firstName?.trim() &&
      formData.lastName?.trim();

    if (!baseFieldsValid) {
      return false;
    }

    // If editing, date and outcome are also required
    if (editingEnquiry) {
      if (!formData.date || !formData.outcome) {
        return false;
      }
    }

    // If "Advertiser In Database" is Yes, organization selection is required
    if ((formData.advertiserInDatabase === true || formData.advertiserInDatabase === 'Yes')) {
      if (!formData.organizationId) {
        return false;
      }
    }

    // If outcome is "Follow Up Later", follow up date is required
    if (formData.outcome === 'Follow Up Later') {
      if (!formData.followUpDate) {
        return false;
      }
    }

    // If outcome is "Not Interested", reason is required
    if (formData.outcome === 'Not Interested') {
      if (!formData.whyNotInterested?.trim()) {
        return false;
      }
    }

    return true;
  };

  const handleSaveEnquiry = async () => {
    try {
      const token = await getToken();
      
      // Prepare the data with proper field mapping
      const enquiryData = {
        ...formData,
        // Map salesConsultant ID properly
        salesConsultantId: formData.salesConsultant,
        // Remove the salesConsultant field to avoid confusion
        salesConsultant: undefined,
        // Convert boolean to string for advertiserInDatabase
        advertiserInDatabase: formData.advertiserInDatabase === true || formData.advertiserInDatabase === 'Yes' ? 'Yes' : 'No',
        // Include organization ID
        organizationId: formData.organizationId || null
      };

      // Data processed and ready for submission

      // Remove undefined fields
      Object.keys(enquiryData).forEach(key => {
        if (enquiryData[key] === undefined) {
          delete enquiryData[key];
        }
      });

      if (editingEnquiry) {
        // Update existing enquiry
        const response = await fetch(`${API_URL}/enquiries/${editingEnquiry.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(enquiryData)
        });

        if (response.ok) {
          const result = await response.json();
          setEnquiries(prev => prev.map(enq => 
            enq.id === editingEnquiry.id ? result.data : enq
          ));
          setSnackbar({ open: true, message: 'Enquiry updated successfully', severity: 'success' });
        } else {
          throw new Error('Failed to update enquiry');
        }
      } else {
        // Create new enquiry
        const response = await fetch(`${API_URL}/enquiries`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(enquiryData)
        });

        if (response.ok) {
          const result = await response.json();
          setEnquiries(prev => [result.data, ...prev]);
          setSnackbar({ open: true, message: 'Enquiry created successfully', severity: 'success' });
        } else {
          throw new Error('Failed to create enquiry');
        }
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving enquiry:', error);
      setSnackbar({ open: true, message: 'Error saving enquiry', severity: 'error' });
    }
  };

  const handleDeleteEnquiry = async (id) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/enquiries/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setEnquiries(prev => prev.filter(enq => enq.id !== id));
        setSnackbar({ open: true, message: 'Enquiry deleted successfully', severity: 'success' });
      } else {
        throw new Error('Failed to delete enquiry');
      }
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      setSnackbar({ open: true, message: 'Error deleting enquiry', severity: 'error' });
    }
  };

  const handleSalesConsultantChange = (enquiryId, newSalesConsultantId) => {
    // Find the consultant name
    const consultant = salesConsultants.find(sc => sc.id === parseInt(newSalesConsultantId));
    
    // Show confirmation dialog
    setConsultantConfirmDialog({
      open: true,
      enquiryId: enquiryId,
      consultantId: newSalesConsultantId,
      consultantName: consultant ? consultant.name : 'Unknown'
    });
  };

  const handleConfirmConsultantAssignment = async () => {
    const { enquiryId, consultantId } = consultantConfirmDialog;
    
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/enquiries/${enquiryId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          salesConsultantId: consultantId
        })
      });

      if (response.ok) {
        // Update local state
        setEnquiries(prev => prev.map(enq => {
          if (enq.id === enquiryId) {
            const newConsultant = salesConsultants.find(sc => sc.id === parseInt(consultantId));
            return {
              ...enq,
              salesConsultantId: consultantId,
              salesConsultant: newConsultant ? newConsultant.name : enq.salesConsultant
            };
          }
          return enq;
        }));
        setSnackbar({ open: true, message: 'Sales consultant assigned successfully', severity: 'success' });
      } else {
        throw new Error('Failed to update sales consultant');
      }
    } catch (error) {
      console.error('Error updating sales consultant:', error);
      setSnackbar({ open: true, message: 'Error assigning sales consultant', severity: 'error' });
    } finally {
      // Close confirmation dialog
      setConsultantConfirmDialog({
        open: false,
        enquiryId: null,
        consultantId: null,
        consultantName: ''
      });
    }
  };

  const handleCancelConsultantAssignment = () => {
    setConsultantConfirmDialog({
      open: false,
      enquiryId: null,
      consultantId: null,
      consultantName: ''
    });
  };

  const handleConvertToBusiness = (enquiry) => {
    // Pre-fill the organization form with enquiry data
    setOrganizationFormData({
      name: enquiry.businessName || '',
      businessType: 'Business', // Don't use campaignType
      primaryName: `${enquiry.firstName || ''} ${enquiry.lastName || ''}`.trim(),
      primaryEmail: enquiry.email || '',
      primaryPhone: enquiry.phone || '',
      generalEmail: enquiry.email || '',
      generalPhone: enquiry.phone || '',
      address: '',
      suburb: '',
      state: '',
      postcode: '',
      notes: `Created from enquiry (${enquiry.leadMethod}). ${enquiry.salesNotes || enquiry.customerNotes || ''}`.trim(),
      isBusiness: true,
      isSchool: false,
      isActive: true
    });
    
    setEnquiryToConvert(enquiry);
    setConvertDialogOpen(true);
  };

  const handleCreateOrganization = async () => {
    try {
      const token = await getToken();

      const response = await fetch(`${API_URL}/organizations`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(organizationFormData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update only the necessary fields to avoid data type issues
        const updateEnquiryData = {
          advertiserInDatabase: true,
          organizationId: result.organizationId // Link the enquiry to the created organization
        };

        const updateResponse = await fetch(`${API_URL}/enquiries/${enquiryToConvert.id}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateEnquiryData)
        });

        // Update the enquiry in local state immediately
        setEnquiries(prev => prev.map(enq => 
          enq.id === enquiryToConvert.id 
            ? { 
                ...enq, 
                advertiserInDatabase: 'Yes', 
                organizationId: result.organizationId,
                linkedOrganizationName: organizationFormData.name
              } 
            : enq
        ));

        // Close dialog and show success message
        setConvertDialogOpen(false);
        setEnquiryToConvert(null);
        resetOrganizationFormData();
        
        setSnackbar({ 
          open: true, 
          message: `Business "${organizationFormData.name}" created successfully!`, 
          severity: 'success' 
        });
        
        // Reload organizations list to include the newly created organization
        loadOrganizations();
        
        // Reload enquiries to ensure data consistency with backend
        loadEnquiries();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create business');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      setSnackbar({ 
        open: true, 
        message: `Error creating business: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  const handleCancelConvert = () => {
    setConvertDialogOpen(false);
    setEnquiryToConvert(null);
    resetOrganizationFormData();
  };

  const handleConvertAddressChange = (addressData) => {
    setOrganizationFormData(prev => ({
      ...prev,
      formattedAddress: addressData.fullAddress || prev.formattedAddress,
      address: addressData.streetAddress || addressData.fullAddress || prev.address,
      suburb: addressData.suburb || prev.suburb,
      state: addressData.state || addressData.region || prev.state,
      postcode: addressData.postcode || prev.postcode,
      latitude: addressData.coordinates?.lat || prev.latitude,
      longitude: addressData.coordinates?.lng || prev.longitude,
    }));
  };

  const resetOrganizationFormData = () => {
    setOrganizationFormData({
      name: '',
      businessType: '',
      primaryName: '',
      primaryEmail: '',
      primaryPhone: '',
      generalEmail: '',
      generalPhone: '',
      address: '',
      formattedAddress: '',
      suburb: '',
      state: '',
      postcode: '',
      latitude: null,
      longitude: null,
      notes: '',
      isBusiness: true,
      isSchool: false,
      isActive: true
    });
  };



  const handleCreateOrder = (enquiry) => {
    // Use organizationId if available, otherwise try name matching as fallback
    let matchingOrg = null;
    
    if (enquiry.organizationId) {
      // Direct lookup by organization ID (preferred method)
      matchingOrg = organizations.find(org => org.id === enquiry.organizationId);
    } else {
      // Fallback: Find matching organization by business name
      matchingOrg = organizations.find(org => 
        org.name && enquiry.businessName && 
        org.name.toLowerCase().trim() === enquiry.businessName.toLowerCase().trim()
      );
    }

    if (matchingOrg) {
      setSelectedOrganization(matchingOrg);
      // Pre-fill the order form with enquiry and organization data
      setOrderFormData({
        organization_id: matchingOrg.id,
        business_number: matchingOrg.businessNumber || '',
        business_name: matchingOrg.name || enquiry.businessName || '',
        business_type: matchingOrg.businessType || '',
        orderType: 'ad',
        productType: 'Lead', // Default to "Lead" as requested
        notes: `Order from enquiry (${enquiry.leadMethod}). ${collectAllNotesFromEnquiry(enquiry)}`.trim(),
        designNotes: '',
        audiences: [{
          organization_id: '',
          channel: '',
          adType: '',
          adGroup: 1,
          startDate: getCurrentDateForInput(),
          endDate: getFutureDateForInput(365), // Default 1 year from now
          price: enquiry.invoiceAmount || '',
          discount: '0',
          discountType: '$',
          eLinkType: 'None',
          eLinkContent: ''
        }]
      });
    } else {
      const warningMessage = enquiry.organizationId 
        ? `Warning: Organisation ID ${enquiry.organizationId} not found in current organizations list.`
        : `Warning: No organization found matching "${enquiry.businessName}". Please verify the business exists in your database.`;
        
      setSnackbar({ 
        open: true, 
        message: warningMessage, 
        severity: 'warning' 
      });
      
      // Still allow creating order but with empty organization
      setSelectedOrganization(null);
      setOrderFormData({
        organization_id: '',
        business_number: '',
        business_name: enquiry.businessName || '',
        business_type: '',
        orderType: 'ad',
        productType: 'Lead', // Default to "Lead" as requested
        notes: `Order from enquiry (${enquiry.leadMethod}). ${collectAllNotesFromEnquiry(enquiry)}`.trim(),
        designNotes: '',
        audiences: [{
          organization_id: '',
          channel: '',
          adType: '',
          adGroup: 1,
          startDate: getCurrentDateForInput(),
          endDate: getFutureDateForInput(365), // Default 1 year from now
          price: enquiry.invoiceAmount || '',
          discount: '0',
          discountType: '$',
          eLinkType: 'None',
          eLinkContent: ''
        }]
      });
    }
    
    setEnquiryForOrder(enquiry);
    setCreateOrderDialogOpen(true);
  };

  const handleSubmitOrder = async () => {
    try {
      const token = await getToken();

      // Validate that we have required fields
      if (!orderFormData.organization_id) {
        setSnackbar({ 
          open: true, 
          message: 'Please select an advertiser organization', 
          severity: 'error' 
        });
        return;
      }

      // Validate audiences
      if (!orderFormData.audiences || orderFormData.audiences.length === 0) {
        setSnackbar({ 
          open: true, 
          message: 'Please add at least one audience', 
          severity: 'error' 
        });
        return;
      }

      for (let i = 0; i < orderFormData.audiences.length; i++) {
        const audience = orderFormData.audiences[i];
        if (!audience.organization_id) {
          setSnackbar({ 
            open: true, 
            message: `Please select a target organization (school) for Audience ${i + 1}`, 
            severity: 'error' 
          });
          return;
        }
        if (!audience.channel) {
          setSnackbar({ 
            open: true, 
            message: `Please select a channel for Audience ${i + 1}`, 
            severity: 'error' 
          });
          return;
        }
        if (!audience.adType) {
          setSnackbar({ 
            open: true, 
            message: `Please select an ad type for Audience ${i + 1}`, 
            severity: 'error' 
          });
          return;
        }
      }

              // Create the ad order data structure
        const orderData = { 
          orderType: 'ad',
          
          // Main order fields - map businessNumber to organization
          organization: orderFormData.organization_id,
          productType: orderFormData.productType || '',
          notes: `[FROM ENQUIRY #${enquiryForOrder.id}] ${orderFormData.notes || ''}`.trim(),
          designNotes: orderFormData.designNotes || '',
          totalPrice: 0, // Will be calculated from audiences
          status: 'New',
          
          // Audiences using exact field names from database
          audiences: orderFormData.audiences.map(audience => {
            return {
              organization: audience.organization_id,
              channel: audience.channel || '',
              adType: audience.adType || '',
                        startDate: audience.startDate || getCurrentDateForInput(),
          endDate: audience.endDate || getCurrentDateForInput(),
              price: parseFloat(audience.price) || 0,
              discount: parseFloat(audience.discount) || 0,
              discountType: audience.discountType || '$',
              eLinkType: audience.eLinkType || '',
              eLinkContent: audience.eLinkContent || ''
            };
          })
        };

      // Use the ad-orders endpoint specifically
      const response = await fetch(`${API_URL}/ad-orders`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Calculate total price from audiences for enquiry update
        const totalPrice = orderData.audiences.reduce((sum, audience) => {
          const price = parseFloat(audience.price) || 0;
          const discount = parseFloat(audience.discount) || 0;
          const discountAmount = audience.discountType === '%' 
            ? (price * discount / 100) 
            : discount;
          return sum + (price - discountAmount);
        }, 0);

        // Update only the necessary fields to avoid data type issues
        const updateEnquiryData = {
          outcome: 'Ordered',
          invoiceAmount: totalPrice
        };

        const updateResponse = await fetch(`${API_URL}/enquiries/${enquiryForOrder.id}`, {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateEnquiryData)
        });

        // Close dialog and show success message
        handleCancelOrder();
        
        setSnackbar({ 
          open: true, 
          message: `Ad order created successfully for "${orderFormData.business_name}"!`, 
          severity: 'success' 
        });
        
        // Reload enquiries to show updated status
        loadEnquiries();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create ad order');
      }
    } catch (error) {
      console.error('Error creating ad order:', error);
      setSnackbar({ 
        open: true, 
        message: `Error creating ad order: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  const handleCancelOrder = () => {
    setCreateOrderDialogOpen(false);
    setEnquiryForOrder(null);
    setSelectedOrganization(null);
    setOrderFormData({
      organization_id: '',
      business_number: '',
      business_name: '',
      business_type: '',
      orderType: 'ad',
      productType: '',
      notes: '',
      designNotes: '',
      audiences: [{
        organization_id: '',
        channel: '',
        adType: '',
        adGroup: 1,
        startDate: '',
        endDate: '',
        price: '',
        discount: '0',
        discountType: '$',
        eLinkType: 'None',
        eLinkContent: ''
      }]
    });
  };

  // Audience management functions for the order form
  const handleAudienceChange = (index, field, value) => {
    const newAudiences = [...orderFormData.audiences];
    newAudiences[index] = { ...newAudiences[index], [field]: value };
    setOrderFormData({ ...orderFormData, audiences: newAudiences });
  };

  const addAudience = () => {
    const firstAudience = orderFormData.audiences[0] || {};
    const newAudience = {
      organization_id: '',
      channel: '',
      adType: '',
      adGroup: orderFormData.audiences.length + 1,
      startDate: firstAudience.startDate || '',
      endDate: firstAudience.endDate || '',
      price: '',
      discount: '0',
      discountType: '$',
      eLinkType: firstAudience.eLinkType || 'None',
      eLinkContent: firstAudience.eLinkType !== 'None' ? (firstAudience.eLinkContent || '') : ''
    };
    
    setOrderFormData({
      ...orderFormData,
      audiences: [...orderFormData.audiences, newAudience]
    });
  };

  const removeAudience = (index) => {
    const newAudiences = orderFormData.audiences.filter((_, i) => i !== index);
    setOrderFormData({ ...orderFormData, audiences: newAudiences });
  };

  // Normalise a phone number string for comparison (strip spaces, dashes, parentheses, plus)
  const normalisePhone = (s) => (s || '').replace(/[\s\-().+]/g, '');

  // Build closest-match candidates for the Find Business dialog
  const getClosestMatches = (enquiry) => {
    const businessOrgs = organizations.filter(
      (o) => (o.isBusiness === true || o.isBusiness === 1) && (o.isActive === true || o.isActive === 1)
    );

    const exactEmail = [];
    const exactPhone = [];
    const fuzzyName = [];

    const enquiryEmail = (enquiry.email || '').trim().toLowerCase();
    const enquiryPhone = normalisePhone(enquiry.phone);
    const nameWords = (enquiry.businessName || '')
      .split(/\s+/)
      .filter((w) => w.length > 3)
      .map((w) => w.toLowerCase());

    businessOrgs.forEach((org) => {
      const orgEmail = (org.email || '').trim().toLowerCase();
      const orgPhone = normalisePhone(org.phone || org.generalPhone || org.primaryPhone);

      if (enquiryEmail && orgEmail && orgEmail === enquiryEmail) {
        exactEmail.push(org);
        return;
      }
      if (enquiryPhone && orgPhone && orgPhone === enquiryPhone) {
        exactPhone.push(org);
        return;
      }
      if (nameWords.length > 0) {
        const score = nameWords.filter((w) => org.name.toLowerCase().includes(w)).length;
        if (score > 0) {
          fuzzyName.push({ org, score });
        }
      }
    });

    fuzzyName.sort((a, b) => b.score - a.score);
    return [...exactEmail, ...exactPhone, ...fuzzyName.slice(0, 5).map((x) => x.org)];
  };

  const handleOpenFindBusiness = (enquiry) => {
    setFindBusinessSearch('');
    setFindBusinessSearchResults([]);
    setFindBusinessDialog({ open: true, enquiry });
  };

  const handleCloseFindBusiness = () => {
    setFindBusinessDialog({ open: false, enquiry: null });
    setFindBusinessSearch('');
    setFindBusinessSearchResults([]);
  };

  const handleFindBusinessSearch = async (query) => {
    setFindBusinessSearch(query);
    if (!query.trim()) {
      setFindBusinessSearchResults([]);
      return;
    }
    setFindBusinessSearchLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${API_URL}/organizations/search?query=${encodeURIComponent(query)}&type=business`,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        const result = await response.json();
        setFindBusinessSearchResults(Array.isArray(result) ? result : result.data || []);
      }
    } catch (err) {
      console.error('Organization search error:', err);
    } finally {
      setFindBusinessSearchLoading(false);
    }
  };

  const handleLinkOrganization = async (enquiry, org) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/enquiries/${enquiry.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ advertiserInDatabase: 'Yes', organizationId: org.id })
      });
      if (response.ok) {
        setEnquiries((prev) =>
          prev.map((enq) =>
            enq.id === enquiry.id
              ? { ...enq, advertiserInDatabase: 'Yes', organizationId: org.id, linkedOrganizationName: org.name }
              : enq
          )
        );
        setSnackbar({ open: true, message: `Linked to "${org.name}" successfully`, severity: 'success' });
        handleCloseFindBusiness();
      } else {
        throw new Error('Failed to link organization');
      }
    } catch (err) {
      console.error('Error linking organization:', err);
      setSnackbar({ open: true, message: 'Error linking organization', severity: 'error' });
    }
  };

  const handleConfirmNewBusiness = () => {
    setSnackbar({ open: true, message: 'Confirmed as a new business. Use "Convert to Business" when ready.', severity: 'info' });
    handleCloseFindBusiness();
  };

  const getOutcomeColor = (outcome) => {
    const colors = {
      'New': 'primary',
      'Follow Up Later': 'warning',
      'Offered': 'info',
      'Not Interested': 'error',
      'Cancelled After Order': 'error'
    };
    return colors[outcome] || 'default';
  };

  // Helper function to calculate date range
  const getDateRangeFilter = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const startDate = new Date(year, month - 1, 1); // First day of selected month
    
    // Calculate end date based on date range
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + parseInt(dateRange));
    endDate.setDate(0); // Last day of the previous month (end of range)
    
    return { startDate, endDate };
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    // Search filter
    const matchesSearch = (
      enquiry.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Outcome filter
    const matchesOutcome = outcomeFilter === 'all' || enquiry.outcome === outcomeFilter;
    
    // Date range filter
    const { startDate, endDate } = getDateRangeFilter();
    const enquiryDate = new Date(enquiry.date);
    const matchesDateRange = enquiryDate >= startDate && enquiryDate <= endDate;
    
    return matchesSearch && matchesOutcome && matchesDateRange;
  });

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Enquiries Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track and manage all enquiries from initial contact to conversion
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={enquiryListTab}
          onChange={(_, v) => setEnquiryListTab(v)}
          aria-label="Enquiry type"
        >
          <Tab label="Advertising & sales enquiries" value="sales" />
          <Tab label="Website general contact" value="website_general" />
          <Tab label="School Enquiries" value="school_enquiries" />
        </Tabs>
      </Box>

      {/* Summary Cards */}
      {enquiryListTab === 'sales' && (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Enquiries
              </Typography>
              <Typography variant="h4">
                {enquiries.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                New Enquiries
              </Typography>
              <Typography variant="h4">
                {enquiries.filter(e => e.outcome === 'New').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Conversions
              </Typography>
              <Typography variant="h4">
                {enquiries.filter(e => e.outcome === 'Ordered').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Conversion Rate
              </Typography>
              <Typography variant="h4">
                {enquiries.length > 0 ? 
                  Math.round((enquiries.filter(e => e.outcome === 'Ordered').length / enquiries.length) * 100) : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* Controls */}
      {enquiryListTab === 'sales' && (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Enquiries List</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />}>
              Export
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              New Enquiry
            </Button>
          </Box>
        </Box>

        {/* Search and Filter */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            {/* Search */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search enquiries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Outcome Filter */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Outcome</InputLabel>
                <Select
                  value={outcomeFilter}
                  onChange={(e) => setOutcomeFilter(e.target.value)}
                  label="Outcome"
                >
                  <MenuItem value="all">All Outcomes</MenuItem>
                  {outcomes.map(outcome => (
                    <MenuItem key={outcome} value={outcome}>{outcome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Date Range */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Date Range"
                >
                  <MenuItem value="1">1 Month</MenuItem>
                  <MenuItem value="3">3 Months</MenuItem>
                  <MenuItem value="6">6 Months</MenuItem>
                  <MenuItem value="12">12 Months</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Starting Month */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="month"
                label="Starting Month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>

          {/* Filter Summary */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing:
            </Typography>
            <Chip 
              label={`${filteredEnquiries.length} enquiries`} 
              color="primary" 
              size="small" 
            />
            {outcomeFilter !== 'all' && (
              <Chip 
                label={`Outcome: ${outcomeFilter}`}
                onDelete={() => setOutcomeFilter('all')}
                size="small"
              />
            )}
            <Chip 
              label={`${dateRange} month${dateRange === '1' ? '' : 's'} from ${new Date(selectedMonth + '-01').toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}`}
              size="small"
              variant="outlined"
            />
          </Box>
        </Paper>

        {/* Enquiries Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Lead Method</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Campaign Area/Schools</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>Follow Up Date</TableCell>
                <TableCell>Sales Consultant</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEnquiries.map((enquiry) => (
                <TableRow key={enquiry.id} hover>
                  <TableCell>{formatDate(enquiry.date)}</TableCell>
                  <TableCell>{enquiry.leadMethod}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {enquiry.businessName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {enquiry.firstName} {enquiry.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {enquiry.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{enquiry.campaignType}</TableCell>
                  <TableCell>
                    <Chip 
                      label={enquiry.outcome} 
                      color={getOutcomeColor(enquiry.outcome)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    {enquiry.outcome === 'Follow Up Later' && enquiry.followUpDate ? (
                      (() => {
                        const followUpDate = new Date(enquiry.followUpDate);
                        const today = new Date();
                        const isOverdue = followUpDate < today;
                        const isToday = followUpDate.toDateString() === today.toDateString();
                        
                        const color = isOverdue ? 'error' : isToday ? 'warning' : 'primary';
                        const tooltipText = isOverdue 
                          ? `Follow up was due on ${formatDate(enquiry.followUpDate)} (Overdue)`
                          : isToday 
                            ? `Follow up is due today (${formatDate(enquiry.followUpDate)})`
                            : `Follow up scheduled for ${formatDate(enquiry.followUpDate)}`;
                        
                        return (
                          <Tooltip title={tooltipText}>
                            <Typography 
                              variant="body2" 
                              color={color} 
                              sx={{ cursor: 'pointer', fontWeight: isOverdue || isToday ? 600 : 400 }}
                            >
                              {formatDate(enquiry.followUpDate)}
                            </Typography>
                          </Tooltip>
                        );
                      })()
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {enquiry.salesConsultantId ? (
                      // Show consultant name as text if assigned
                      <Typography variant="body2">
                        {enquiry.salesConsultant || 'Unknown'}
                      </Typography>
                    ) : (
                      // Show dropdown only if unassigned
                      <FormControl size="small" fullWidth sx={{ minWidth: 150 }}>
                        <Select
                          value=""
                          onChange={(e) => handleSalesConsultantChange(enquiry.id, e.target.value)}
                          displayEmpty
                          sx={{ 
                            '& .MuiSelect-select': { 
                              py: 0.5,
                              fontSize: '0.875rem'
                            }
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>Select Consultant...</em>
                          </MenuItem>
                          {salesConsultants.map((consultant) => (
                            <MenuItem key={consultant.id} value={consultant.id}>
                              {consultant.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        const existingNotes = [];
                        if (enquiry.salesNotes) {
                          existingNotes.push({
                            id: 'sales',
                            content: enquiry.salesNotes,
                            createdBy: 'Sales Team',
                            createdAt: enquiry.date || new Date().toISOString(),
                            isNew: false,
                            noteType: 'sales'
                          });
                        }
                        if (enquiry.customerNotes) {
                          existingNotes.push({
                            id: 'customer',
                            content: enquiry.customerNotes,
                            createdBy: 'Customer Service',
                            createdAt: enquiry.date || new Date().toISOString(),
                            isNew: false,
                            noteType: 'customer'
                          });
                        }
                        if (enquiry.followUpNotes) {
                          existingNotes.push({
                            id: 'followup',
                            content: enquiry.followUpNotes,
                            createdBy: 'Sales Team',
                            createdAt: enquiry.followUpDate || enquiry.date || new Date().toISOString(),
                            isNew: false,
                            noteType: 'sales' // Follow-up notes are sales-related
                          });
                        }
                        handleOpenNotesDialog('enquiry', enquiry.id.toString(), enquiry.businessName, existingNotes);
                      }}
                      sx={{ 
                        minWidth: 'auto',
                        px: 1,
                        py: 0.5,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        borderRadius: 1,
                        color: (() => {
                          const totalCount = getTotalNoteCount(enquiry);
                          return totalCount > 0 ? 'primary.main' : 'text.secondary';
                        })(),
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      }}
                    >
                      Notes
                      {(() => {
                        const totalCount = getTotalNoteCount(enquiry);
                        return totalCount > 0 ? (
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              px: 0.5,
                              py: 0.1,
                              borderRadius: '50%',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              minWidth: 18,
                              height: 18,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {totalCount}
                          </Box>
                        ) : null;
                      })()}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(enquiry)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Show Find Business + Convert to Business buttons only if NOT in database */}
                      {(enquiry.advertiserInDatabase === false || enquiry.advertiserInDatabase === 'No') && (
                        <>
                          <Tooltip title="Find Business">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleOpenFindBusiness(enquiry)}
                            >
                              <FindInPageIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Convert to Business">
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleConvertToBusiness(enquiry)}
                            >
                              <BusinessIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {/* Show Create Order button if business IS in database */}
                      {(enquiry.advertiserInDatabase === true || enquiry.advertiserInDatabase === 'Yes') && (
                        <Tooltip title="Create Order">
                          <IconButton 
                            size="small" 
                            color="secondary" 
                            onClick={() => handleCreateOrder(enquiry)}
                          >
                            <OrderIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDeleteEnquiry(enquiry.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {enquiryListTab === 'website_general' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6">Website general contact</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={generalStatusFilter}
                  onChange={(e) => setGeneralStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="responded">Responded</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={loadWebsiteGeneralEnquiries} disabled={loadingWebsiteGeneral}>
                Refresh
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Submissions from the public home page contact form. These are not advertiser leads and do not use the sales enquiry workflow.
          </Typography>
          {loadingWebsiteGeneral ? (
            <Typography>Loading�</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Received</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>School / business</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>How they heard</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {websiteGeneralEnquiries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography color="text.secondary">No submissions yet.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    websiteGeneralEnquiries.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{formatDate(row.createdAt)}</TableCell>
                        <TableCell>{row.contactName}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {row.schoolOrBusinessName}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.phone}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>
                          {WEBSITE_GENERAL_SERVICE_LABELS[row.service] || row.service}
                        </TableCell>
                        <TableCell>
                          {WEBSITE_GENERAL_HEAR_LABELS[row.howDidYouHear] || row.howDidYouHear}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Tooltip title={row.message || ''} placement="top-start">
                            <Typography variant="body2" noWrap>
                              {row.message}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={row.status}
                              onChange={(e) => handleWebsiteGeneralStatusChange(row.id, e.target.value)}
                            >
                              <MenuItem value="new">New</MenuItem>
                              <MenuItem value="responded">Responded</MenuItem>
                              <MenuItem value="archived">Archived</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* School Enquiries Tab */}
      {enquiryListTab === 'school_enquiries' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6">School Enquiries</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Search school, contact, email..."
                value={schoolEnquiriesSearch}
                onChange={(e) => setSchoolEnquiriesSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadSchoolEnquiries()}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={schoolEnquiriesStatusFilter}
                  onChange={(e) => setSchoolEnquiriesStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="New">New</MenuItem>
                  <MenuItem value="Contacted">Contacted</MenuItem>
                  <MenuItem value="Demo">Demo</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={loadSchoolEnquiries} disabled={schoolEnquiriesLoading}>
                Refresh
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenSchoolEnquiryDialog()}>
                Add Enquiry
              </Button>
            </Box>
          </Box>
          {schoolEnquiriesLoading ? (
            <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>School Name</TableCell>
                    <TableCell>Products</TableCell>
                    <TableCell>Contact Person</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Demo Date</TableCell>
                    <TableCell>Added</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schoolEnquiries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography color="text.secondary">No school enquiries found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : schoolEnquiries.map((enq) => {
                    const statusColors = { New: 'primary', Contacted: 'info', Demo: 'warning', Ordered: 'success', Cancelled: 'default' };
                    return (
                      <TableRow key={enq.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{enq.school_name}</Typography>
                        </TableCell>
                        <TableCell>{enq.products || '�'}</TableCell>
                        <TableCell>{enq.contact_person || '�'}</TableCell>
                        <TableCell>{enq.phone || '�'}</TableCell>
                        <TableCell>{enq.email || '�'}</TableCell>
                        <TableCell>
                          <Chip label={enq.status} color={statusColors[enq.status] || 'default'} size="small" />
                        </TableCell>
                        <TableCell>
                          {enq.demo_date ? (
                            <Chip label={formatDate(enq.demo_date)} size="small" color="warning" variant="outlined" />
                          ) : (
                            <Typography variant="caption" color="text.disabled">�</Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(enq.created_at)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {/* Status progression buttons */}
                            {enq.status === 'New' && (
                              <Button size="small" variant="outlined" color="info"
                                sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, textTransform: 'none' }}
                                onClick={() => handleQuickStatusChange(enq, 'Contacted')}>
                                Mark Contacted
                              </Button>
                            )}
                            {enq.status === 'Contacted' && (
                              <Button size="small" variant="outlined" color="warning"
                                sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, textTransform: 'none' }}
                                onClick={() => setDemoDateDialog({ open: true, enquiry: enq, date: '' })}>
                                Set Demo
                              </Button>
                            )}
                            {enq.status === 'Demo' && (
                              <Button size="small" variant="contained" color="success"
                                sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, textTransform: 'none' }}
                                onClick={() => setCreateOrderDialog({ open: true, enquiry: enq, loading: false, formUrl: null, orderId: null })}>
                                Create School Order
                              </Button>
                            )}
                            {/* Edit */}
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleOpenSchoolEnquiryDialog(enq)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* Cancel */}
                            {enq.status !== 'Cancelled' && enq.status !== 'Ordered' && (
                              <Tooltip title="Cancel enquiry">
                                <IconButton size="small" color="error"
                                  onClick={() => setCancelEnquiryDialog({ open: true, enquiry: enq })}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Demo Date Dialog */}
      <Dialog open={demoDateDialog.open} onClose={() => setDemoDateDialog({ open: false, enquiry: null, date: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Set Demo Date � {demoDateDialog.enquiry?.school_name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              type="date"
              label="Demo Date"
              InputLabelProps={{ shrink: true }}
              value={demoDateDialog.date}
              onChange={(e) => setDemoDateDialog(prev => ({ ...prev, date: e.target.value }))}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDemoDateDialog({ open: false, enquiry: null, date: '' })}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={!demoDateDialog.date} onClick={handleConfirmDemoDate}>
            Set Demo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create School Order Dialog */}
      <Dialog open={createOrderDialog.open} onClose={() => !createOrderDialog.loading && setCreateOrderDialog({ open: false, enquiry: null, loading: false, formUrl: null, orderId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Create School Order � {createOrderDialog.enquiry?.school_name}</DialogTitle>
        <DialogContent>
          {!createOrderDialog.formUrl ? (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info">
                This will create a new school order for <strong>{createOrderDialog.enquiry?.school_name}</strong> and generate a signup form link. The enquiry will be marked as Ordered.
              </Alert>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                School order #{createOrderDialog.orderId} created! The signup form link has been copied to your clipboard.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', mb: 1 }}>
                {createOrderDialog.formUrl}
              </Typography>
              <Button size="small" variant="outlined" startIcon={<CheckCircleIcon />}
                onClick={() => { try { navigator.clipboard.writeText(createOrderDialog.formUrl); } catch {} setSnackbar({ open: true, message: 'Link copied!', severity: 'success' }); }}>
                Copy Link Again
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOrderDialog({ open: false, enquiry: null, loading: false, formUrl: null, orderId: null })}>
            {createOrderDialog.formUrl ? 'Close' : 'Cancel'}
          </Button>
          {!createOrderDialog.formUrl && (
            <Button variant="contained" color="success" disabled={createOrderDialog.loading} onClick={handleCreateOrderFromEnquiry}>
              {createOrderDialog.loading ? 'Creating...' : 'Create Order'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Enquiry Confirm Dialog */}
      <Dialog open={cancelEnquiryDialog.open} onClose={() => setCancelEnquiryDialog({ open: false, enquiry: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Enquiry</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel the enquiry for <strong>{cancelEnquiryDialog.enquiry?.school_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelEnquiryDialog({ open: false, enquiry: null })}>Keep</Button>
          <Button variant="contained" color="error" onClick={handleConfirmCancelEnquiry}>Cancel Enquiry</Button>
        </DialogActions>
      </Dialog>

      {/* School Enquiry Add/Edit Dialog */}
      <Dialog open={schoolEnquiryDialog.open} onClose={() => setSchoolEnquiryDialog({ open: false, editing: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{schoolEnquiryDialog.editing ? 'Edit School Enquiry' : 'Add School Enquiry'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="School Name"
                value={schoolEnquiryForm.school_name}
                onChange={(e) => setSchoolEnquiryForm({ ...schoolEnquiryForm, school_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Products</InputLabel>
                <Select
                  multiple
                  value={schoolEnquiryForm.products}
                  onChange={(e) => setSchoolEnquiryForm({ ...schoolEnquiryForm, products: e.target.value })}
                  input={<OutlinedInput label="Products" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((val) => <Chip key={val} label={val} size="small" />)}
                    </Box>
                  )}
                >
                  {['Paper', 'ePublisher', 'SchoolLink'].map((product) => (
                    <MenuItem key={product} value={product}>
                      <Checkbox checked={schoolEnquiryForm.products.includes(product)} />
                      <ListItemText primary={product} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={schoolEnquiryForm.contact_person}
                onChange={(e) => setSchoolEnquiryForm({ ...schoolEnquiryForm, contact_person: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={schoolEnquiryForm.status}
                  onChange={(e) => setSchoolEnquiryForm({ ...schoolEnquiryForm, status: e.target.value })}
                >
                  <MenuItem value="New">New</MenuItem>
                  <MenuItem value="Contacted">Contacted</MenuItem>
                  <MenuItem value="Demo">Demo</MenuItem>
                  <MenuItem value="Ordered">Ordered</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={schoolEnquiryForm.phone}
                onChange={(e) => setSchoolEnquiryForm({ ...schoolEnquiryForm, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={schoolEnquiryForm.email}
                onChange={(e) => setSchoolEnquiryForm({ ...schoolEnquiryForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={schoolEnquiryForm.notes}
                onChange={(e) => setSchoolEnquiryForm({ ...schoolEnquiryForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchoolEnquiryDialog({ open: false, editing: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSchoolEnquiry}>
            {schoolEnquiryDialog.editing ? 'Save Changes' : 'Add Enquiry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* School Enquiry Delete Confirm Dialog */}
      <Dialog open={schoolEnquiryDeleteDialog.open} onClose={() => setSchoolEnquiryDeleteDialog({ open: false, id: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete School Enquiry</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this school enquiry? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchoolEnquiryDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteSchoolEnquiry}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEnquiry ? 'Edit Enquiry' : 'New Enquiry'}
        </DialogTitle>
        <DialogContent>
          {!editingEnquiry && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" color="info.contrastText">
                This enquiry will be created with today's date ({formatDate(new Date())}) and status "New"
              </Typography>
            </Box>
          )}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {editingEnquiry && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Outcome</InputLabel>
                    <Select
                      value={formData.outcome}
                      onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                      label="Outcome"
                    >
                      {outcomes.map(outcome => (
                        <MenuItem key={outcome} value={outcome}>{outcome}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Method of Lead</InputLabel>
                <Select
                  value={formData.leadMethod}
                  onChange={(e) => setFormData({ ...formData, leadMethod: e.target.value })}
                  label="Method of Lead"
                >
                  {leadMethods.map(method => (
                    <MenuItem key={method} value={method}>{method}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Advertiser In Database</InputLabel>
                <Select
                  value={formData.advertiserInDatabase}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const inDatabase = newValue === true || newValue === 'Yes';
                    if (!inDatabase) {
                      setSelectedEnquiryOrg(null);
                    }
                    setFormData({ 
                      ...formData, 
                      advertiserInDatabase: newValue,
                      // A database advertiser is chosen from search. A new one is typed.
                      organizationId: inDatabase ? formData.organizationId : '',
                      businessName: inDatabase ? (selectedEnquiryOrg?.name || '') : formData.businessName
                    });
                  }}
                  label="Advertiser In Database"
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Organisation Selection - Show only when "Yes" is selected */}
            {(formData.advertiserInDatabase === true || formData.advertiserInDatabase === 'Yes') && (
              <Grid item xs={12} md={6}>
                <EnhancedSearch
                  label="Select Business"
                  placeholder="Search businesses..."
                  endpoint="/organizations/search?type=business&includeInactive=1"
                  value={selectedEnquiryOrg}
                  onSelect={(newValue) => {
                    setSelectedEnquiryOrg(newValue || null);
                    setFormData({
                      ...formData,
                      organizationId: newValue ? newValue.id.toString() : '',
                      businessName: newValue ? newValue.name : ''
                    });
                  }}
                  limitResults={0}
                  minChars={2}
                  textFieldProps={{ required: true }}
                  filterOptions={(options) => options.filter(opt =>
                    opt.isBusiness === true || opt.isBusiness === 1
                  )}
                />
              </Grid>
            )}

            {formData.outcome === 'Follow Up Later' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Follow Up Date"
                    type="date"
                    value={formData.followUpDate}
                    onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Follow Up Notes"
                    multiline
                    rows={3}
                    value={formData.followUpNotes || ''}
                    onChange={(e) => setFormData({ ...formData, followUpNotes: e.target.value })}
                    placeholder="Add notes about what needs to be followed up on, context, or specific actions required..."
                    helperText="Optional: Add any context or notes for the follow-up"
                  />
                </Grid>
              </>
            )}

            {formData.outcome === 'Not Interested' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Why Not Interested</InputLabel>
                  <Select
                    value={formData.whyNotInterested}
                    onChange={(e) => setFormData({ ...formData, whyNotInterested: e.target.value })}
                    label="Why Not Interested"
                  >
                    {notInterestedReasons.map(reason => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Name"
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                required
                disabled={formData.advertiserInDatabase === true || formData.advertiserInDatabase === 'Yes'}
                helperText={
                  (formData.advertiserInDatabase === true || formData.advertiserInDatabase === 'Yes')
                    ? 'Filled from the selected business'
                    : 'Type the business name'
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={5}
                label="Campaign Area/Schools"
                value={formData.campaignType}
                onChange={(e) => setFormData({ ...formData, campaignType: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Sales Consultant</InputLabel>
                <Select
                  value={formData.salesConsultant || ''}
                  onChange={(e) => setFormData({ ...formData, salesConsultant: e.target.value })}
                  label="Sales Consultant"
                >
                  <MenuItem value="">
                    <em>Select Sales Consultant</em>
                  </MenuItem>
                  {salesConsultants.map(consultant => (
                    <MenuItem key={consultant.id || consultant.name} value={consultant.id}>
                      {consultant.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {formData.outcome === 'Ordered' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Invoice Amount ($)"
                  type="number"
                  value={formData.invoiceAmount}
                  onChange={(e) => setFormData({ ...formData, invoiceAmount: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sales Notes"
                value={formData.salesNotes}
                onChange={(e) => setFormData({ ...formData, salesNotes: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Customer Notes"
                value={formData.customerNotes}
                onChange={(e) => setFormData({ ...formData, customerNotes: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveEnquiry}
            disabled={!isFormValid()}
          >
            {editingEnquiry ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Business Dialog */}
      <Dialog open={convertDialogOpen} onClose={handleCancelConvert} maxWidth="md" fullWidth>
        <DialogTitle>
          Convert to Business Organisation
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review and complete the information below to create a new business organization from this enquiry.
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Name"
                value={organizationFormData.name}
                onChange={(e) => setOrganizationFormData({ ...organizationFormData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Type"
                value={organizationFormData.businessType}
                onChange={(e) => setOrganizationFormData({ ...organizationFormData, businessType: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Primary Contact Name"
                value={organizationFormData.primaryName}
                onChange={(e) => setOrganizationFormData({ ...organizationFormData, primaryName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Primary Email"
                type="email"
                value={organizationFormData.primaryEmail}
                onChange={(e) => setOrganizationFormData({ ...organizationFormData, primaryEmail: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Primary Phone"
                value={organizationFormData.primaryPhone}
                onChange={(e) => setOrganizationFormData({ ...organizationFormData, primaryPhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="General Email"
                type="email"
                value={organizationFormData.generalEmail}
                onChange={(e) => setOrganizationFormData({ ...organizationFormData, generalEmail: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Address
              </Typography>
              <GoogleMapsAddressInput
                value={organizationFormData.address || ''}
                onChange={(address) => setOrganizationFormData(prev => ({ ...prev, address }))}
                onCoordinateChange={handleConvertAddressChange}
                initialAddress={organizationFormData.address || ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={organizationFormData.notes}
                onChange={(e) => setOrganizationFormData({ ...organizationFormData, notes: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConvert}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCreateOrganization}
            disabled={!organizationFormData.name.trim()}
          >
            Create Business
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Order Dialog */}
      <Dialog 
        open={createOrderDialogOpen} 
        onClose={handleCancelOrder} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { 
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Create Advertising Order from Enquiry
            </Typography>
            {selectedOrganization && (
              <Chip 
                label={`Business: ${selectedOrganization.name}`} 
                color="primary" 
                variant="outlined" 
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {selectedOrganization 
              ? `Creating order for ${selectedOrganization.name}. Business information is pre-filled and locked.`
              : `No matching organization found for "${enquiryForOrder?.businessName}". Please select the correct business.`
            }
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
          <AdvertisingOrderForm 
            formData={orderFormData} 
            setFormData={setOrderFormData} 
            organizations={organizations}
            handleAudienceChange={handleAudienceChange}
            addAudience={addAudience}
            removeAudience={removeAudience}
            lockedOrganization={selectedOrganization}
          />
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, p: 2 }}>
          <Button onClick={handleCancelOrder}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitOrder}
            disabled={
              !orderFormData.organization_id || 
              !orderFormData.productType || 
              !orderFormData.audiences[0]?.organization_id || 
              !orderFormData.audiences[0]?.channel || 
              !orderFormData.audiences[0]?.startDate || 
              !orderFormData.audiences[0]?.endDate || 
              !orderFormData.audiences[0]?.price ||
              !checkAddonDurationRequirements(orderFormData.audiences) ||
              !checkOrderTopSpotDurationRequirements(orderFormData.audiences)
            }
            color="primary"
          >
            Create Ad Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Find Business Dialog */}
      <Dialog
        open={findBusinessDialog.open}
        onClose={handleCloseFindBusiness}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FindInPageIcon color="warning" />
            <Typography variant="h6">Find Business</Typography>
          </Box>
          {findBusinessDialog.enquiry && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Checking if <strong>{findBusinessDialog.enquiry.businessName || '(no business name)'}</strong> is already in the system
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {/* Closest matches section */}
          {findBusinessDialog.enquiry && (() => {
            const matches = getClosestMatches(findBusinessDialog.enquiry);
            return (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                  Closest matches
                </Typography>
                {matches.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No close matches found based on email, phone, or business name.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {matches.map((org) => (
                      <React.Fragment key={org.id}>
                        <ListItem
                          alignItems="flex-start"
                          sx={{ px: 0, py: 0.5 }}
                          secondaryAction={
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleLinkOrganization(findBusinessDialog.enquiry, org)}
                              sx={{ ml: 1 }}
                            >
                              Link
                            </Button>
                          }
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={600}>
                                {org.name}
                              </Typography>
                            }
                            secondary={
                              <Box component="span" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.25 }}>
                                {(org.email || org.generalEmail) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {org.email || org.generalEmail}
                                  </Typography>
                                )}
                                {(org.phone || org.generalPhone) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {org.phone || org.generalPhone}
                                  </Typography>
                                )}
                                {org.suburb && (
                                  <Typography variant="caption" color="text.secondary">
                                    {org.suburb}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            );
          })()}

          {/* Manual search section */}
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
            Search all businesses
          </Typography>
          <TextField
            fullWidth
            placeholder="Type a name, email, or suburb..."
            value={findBusinessSearch}
            onChange={(e) => handleFindBusinessSearch(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {findBusinessSearchLoading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                </InputAdornment>
              )
            }}
            sx={{ mb: 1 }}
          />
          {findBusinessSearch.trim() && (
            findBusinessSearchResults.length === 0 && !findBusinessSearchLoading ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                No businesses found for "{findBusinessSearch}".
              </Typography>
            ) : (
              <List dense disablePadding>
                {findBusinessSearchResults.map((org) => (
                  <React.Fragment key={org.id}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{ px: 0, py: 0.5 }}
                      secondaryAction={
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleLinkOrganization(findBusinessDialog.enquiry, org)}
                          sx={{ ml: 1 }}
                        >
                          Link
                        </Button>
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600}>
                            {org.name}
                          </Typography>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.25 }}>
                            {(org.email || org.generalEmail) && (
                              <Typography variant="caption" color="text.secondary">
                                {org.email || org.generalEmail}
                              </Typography>
                            )}
                            {(org.phone || org.generalPhone) && (
                              <Typography variant="caption" color="text.secondary">
                                {org.phone || org.generalPhone}
                              </Typography>
                            )}
                            {org.suburb && (
                              <Typography variant="caption" color="text.secondary">
                                {org.suburb}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<LinkOffIcon />}
            onClick={handleConfirmNewBusiness}
          >
            New Business
          </Button>
          <Button onClick={handleCloseFindBusiness} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog */}
      <NotesDialog
        open={notesDialog.open}
        onClose={handleCloseNotesDialog}
        title={notesDialog.title}
        entityType={notesDialog.entityType}
        entityId={notesDialog.entityId}
        initialNotes={notesDialog.notes}
        onNotesUpdate={handleNotesUpdate}
      />

      {/* Sales Consultant Assignment Confirmation Dialog */}
      <Dialog 
        open={consultantConfirmDialog.open} 
        onClose={handleCancelConsultantAssignment}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Sales Consultant Assignment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to assign this enquiry to <strong>{consultantConfirmDialog.consultantName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConsultantAssignment} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmConsultantAssignment} 
            variant="contained" 
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Enquiries; 